import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Settings2 } from 'lucide-react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Card from '../../../components/ACS-Dashboard/ACS-Blocks/Card';
import ScrollableContainer from '../../../components/ACS-Dashboard/ACS-Blocks/ScrollableContainer';
import httpClient from '../../../api/httpClient';
import { selectModuleTabs } from '../../../redux/selectors/tabsSelectors';
import {
  addTab,
  setActiveTab,
  setAdvancedFilters,
  setListViewState,
  setStatusFilter,
} from '../../../redux/slices/shipmentSlice';
import { buildShipmentSearchPayload } from './TabContent/shipmentShared';
import { FILTER_TABS } from './TabContent/Utils/sortableColumns';
import { useShipmentStatuses } from './TabContent/Utils/shipmentStatuses';
import {
  describeAggregation,
  describeComparePeriod,
  describeWidgetFilters,
  findTabForWidget,
  formatAggregateValue,
  hasAdvancedFilters,
  normalizeFilters,
  OVERVIEW_TAB_CONTENT_TYPE,
  useOverviewWidgets,
  widgetKey,
  widgetValueKey,
} from './Overview/overviewWidgets';
import OverviewWidgetsModal from './Overview/OverviewWidgetsModal';

/**
 * The Shipment Overview strip: one clickable widget per status by default, or
 * whatever set of status and advanced-filter widgets the user has saved. Each
 * shows how many shipments match it, and opens a tab listing exactly those.
 */
const ShipmentsList = ({ isCollapsed }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { tabs } = useSelector(selectModuleTabs('shipments'));
  const { apiStatuses } = useShipmentStatuses();
  const { widgets, loading: widgetsLoading, error: widgetsError, saveWidgets, resetWidgets } =
    useOverviewWidgets({ apiStatuses });

  const [isManaging, setIsManaging] = useState(false);
  // { [widgetKey]: number | null } — null means that widget's count request failed.
  const [counts, setCounts] = useState({});
  // { [widgetKey]: { currentCount, previousCount } | null } — only populated for
  // widgets with a comparePeriodMinutes set; null means that request failed.
  const [comparisons, setComparisons] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Refetch only when a widget's definition actually changes, not on every render
  // that hands back a new array.
  const countsSignature = useMemo(
    () =>
      JSON.stringify(
        widgets.map((w) => [
          widgetKey(w),
          w.status ?? null,
          normalizeFilters(w.advancedFilters),
          w.comparePeriodMinutes ?? null,
        ])
      ),
    [widgets]
  );

  // The list endpoint has no counts variant, so ask for a single row per widget
  // and keep only the total. Status and advanced filters go through the same
  // payload builder the table uses, so a widget counts exactly what its tab lists.
  const fetchWidgetCount = useCallback(async (widget, signal) => {
    const basePayload = buildShipmentSearchPayload({
      searchQuery: '',
      filter: widget.status,
      advancedFilters: normalizeFilters(widget.advancedFilters),
      filterType: FILTER_TABS.ALL,
    });

    // Sum/Average aren't row counts, so they go through the same
    // widget-comparison-count endpoint the compare chip uses, just without a
    // date window — its currentValue is the widget's main value.
    if (widget.aggregation && widget.aggregation !== 'COUNT') {
      const { data } = await httpClient.post(
        '/api/shipment-details/widget-comparison-count',
        {
          ...basePayload,
          aggregation: widget.aggregation,
          aggregationField: widget.aggregationField,
        },
        {
          signal,
          headers: {
            'Content-Type': 'application/json',
            'Office-Code': Cookies.get('officeCodes'),
          },
        }
      );
      return data?.currentValue ?? 0;
    }

    const { data } = await httpClient.post(
      '/api/shipment-details/partial-search?page=0&size=1',
      basePayload,
      {
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Office-Code': Cookies.get('officeCodes'),
        },
      }
    );

    return data?.totalElements ?? 0;
  }, []);

  // Same filter as the count above, but ANDs on a createdDate window so the chip
  // can show "+N% vs last week" — only fired for widgets that opted in. The
  // backend runs it as two COUNTs against the widget's own (unmodified) filter.
  const fetchWidgetComparison = useCallback(async (widget, signal) => {
    const payload = {
      ...buildShipmentSearchPayload({
        searchQuery: '',
        filter: widget.status,
        advancedFilters: normalizeFilters(widget.advancedFilters),
        filterType: FILTER_TABS.ALL,
      }),
      comparePeriodMinutes: widget.comparePeriodMinutes,
      aggregation: widget.aggregation,
      aggregationField: widget.aggregationField,
    };

    const { data } = await httpClient.post(
      '/api/shipment-details/widget-comparison-count',
      payload,
      {
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Office-Code': Cookies.get('officeCodes'),
        },
      }
    );

    return data;
  }, []);

  // Counts are only fetched while the overview is open, and refreshed each time it
  // is re-opened so the numbers match the list underneath.
  useEffect(() => {
    if (isCollapsed || !widgets.length) return undefined;

    const controller = new AbortController();
    setIsLoading(true);

    Promise.all(
      widgets.map((widget) =>
        fetchWidgetCount(widget, controller.signal)
          .then((count) => [widgetValueKey(widget), count])
          .catch((err) => {
            if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return null;
            console.error(`Failed to load the ${widget.name} widget count:`, err);
            return [widgetValueKey(widget), null];
          })
      )
    ).then((entries) => {
      if (controller.signal.aborted) return;
      setCounts(Object.fromEntries(entries.filter(Boolean)));
      setIsLoading(false);
    });

    const comparedWidgets = widgets.filter((w) => w.comparePeriodMinutes);
    Promise.all(
      comparedWidgets.map((widget) =>
        fetchWidgetComparison(widget, controller.signal)
          .then((result) => [widgetValueKey(widget), result])
          .catch((err) => {
            if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return null;
            console.error(`Failed to load the ${widget.name} widget comparison:`, err);
            return [widgetValueKey(widget), null];
          })
      )
    ).then((entries) => {
      if (controller.signal.aborted) return;
      setComparisons(Object.fromEntries(entries.filter(Boolean)));
    });

    return () => controller.abort();
    // countsSignature stands in for `widgets` so a re-listed but identical set of
    // widgets doesn't refire every count request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed, countsSignature, fetchWidgetCount, fetchWidgetComparison]);

  const openWidgetTab = (widget) => {
    const key = widgetKey(widget);

    // Its own tab, or the user's pinned tab of the same name — either way the
    // shipments are already on screen, so switch rather than stack a duplicate.
    const existingIndex = findTabForWidget(tabs, widget);
    if (existingIndex >= 0) {
      dispatch(setActiveTab({ index: existingIndex }));
      return;
    }
    const newTabId = `overview-${key.replace(/[^\w-]/g, '')}-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      key,
      name: widget.name,
      contentType: OVERVIEW_TAB_CONTENT_TYPE,
      widgetKey: key,
      status: widget.status ?? null,
      // The tab describes what it opened with, so it still lists the right
      // shipments after a reload — or after the widget itself is edited away.
      advancedFilters: normalizeFilters(widget.advancedFilters),
      isDynamic: true,
      createdAt: Date.now(),
    }));

    // The tab-limit middleware can refuse the tab (and toasts on its own), so only
    // seed the filter state once the tab actually exists.
    const opened = store
      .getState()
      .shipments.dynamicTabs.some((tab) => tab.id === newTabId);
    if (!opened) return;

    dispatch(setStatusFilter(widget.status ?? null));
    dispatch(setListViewState({ stateKey: newTabId, filter: widget.status ?? null, currentPage: 1 }));
    // Advanced filters are per-tab, so the tab opens on the widget's own filter and
    // the toolbar shows its pills — the same state the filter modal would write.
    dispatch(setAdvancedFilters({ stateKey: newTabId, filters: normalizeFilters(widget.advancedFilters) }));
  };

  const formatCount = (widget) => {
    const count = counts[widgetValueKey(widget)];
    if (count === null) return '—';
    // No value stored under this definition yet: it is still on its way.
    if (count === undefined) return '…';
    if (widget.aggregation && widget.aggregation !== 'COUNT') {
      return formatAggregateValue(widget, count) ?? '—';
    }
    return count.toLocaleString();
  };

  // { percentage, comparisonText } for the Card's chip, or null to hide it —
  // widgets that never opted into a comparison show no chip at all, same as today.
  const formatComparison = (widget) => {
    if (!widget.comparePeriodMinutes) return null;
    const comparisonText = describeComparePeriod(widget.comparePeriodMinutes);
    const result = comparisons[widgetValueKey(widget)];

    if (result === undefined) return { percentage: '…', comparisonText };
    if (result === null) return { percentage: '—', comparisonText };

    const { currentValue, previousValue } = result;
    if (!previousValue) {
      return { percentage: currentValue > 0 ? 'New' : '0%', comparisonText };
    }
    const pct = Math.round(((currentValue - previousValue) / previousValue) * 100);
    return { percentage: `${pct >= 0 ? '+' : ''}${pct}%`, comparisonText };
  };

  // Collapsed the widgets are hidden by a zero-height parent; unmounting them keeps
  // invisible cards out of the tab order.
  if (isCollapsed) return null;

  return (
    <div className="p-fluid-1 bg-color-custom-bg-purple">
      <div className="flex items-start gap-fluid-1">
        <ScrollableContainer direction="x" className="scrollbar-none" showArrows>
          {/* Every card takes the height of the tallest — a custom widget's name can
              run to two lines and a ragged row of cards reads as broken — but the row
              is capped so one long name can't push the whole strip down. */}
          <div className="flex max-h-[9rem] items-stretch gap-[clamp(0.5rem,1cqw,1rem)]">
            {widgets.map((widget) => {
              const comparison = formatComparison(widget);
              return (
              <div
                key={widgetKey(widget)}
                className="scroll-snap-align-start flex"
                style={{
                  minWidth: 'clamp(12rem,30cqw,11rem)',
                  flex: '0 0 auto',
                }}
                // Card is a div, so Enter/Space have to be wired up by hand.
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  openWidgetTab(widget);
                }}
              >
                <Card
                  title={widget.name}
                  mainValue={formatCount(widget)}
                  caption={describeAggregation(widget)}
                  showComparison={Boolean(comparison)}
                  percentage={comparison?.percentage}
                  comparisonText={comparison?.comparisonText}
                  buttonText={comparison ? 'View' : undefined}
                  onButtonClick={() => openWidgetTab(widget)}
                  onClick={() => openWidgetTab(widget)}
                  tooltip={`${describeWidgetFilters(widget)} — click to open in a tab`}
                  // h-full fills the stretched row; the title clamps to two lines so a
                  // long one is cut cleanly at the ceiling rather than mid-line.
                  customStyles={`h-full overflow-hidden [&_h2]:line-clamp-2 hover:border-[#223688] dark:hover:border-[#6DE1C2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#223688]/40 ${
                    hasAdvancedFilters(widget) ? 'border-l-2 border-l-[color:#223688] dark:border-l-[color:#6DE1C2]' : ''
                  }`}
                />
              </div>
              );
            })}
          </div>
        </ScrollableContainer>

        <button
          type="button"
          onClick={() => setIsManaging(true)}
          disabled={widgetsLoading}
          className="mt-fluid-1 shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-[#223688] disabled:opacity-50 dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover dark:hover:text-[#6DE1C2]"
          title="Manage widgets"
          aria-label="Manage widgets"
        >
          <Settings2 size={18} />
        </button>
      </div>

      <OverviewWidgetsModal
        isOpen={isManaging}
        widgets={widgets}
        apiStatuses={apiStatuses}
        loading={widgetsLoading}
        error={widgetsError}
        onSave={saveWidgets}
        onReset={resetWidgets}
        onClose={() => setIsManaging(false)}
      />
    </div>
  );
};

export default ShipmentsList;
